import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Tabs,
  Tab,
  Alert,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  IconButton,
  Snackbar,
  Switch,
} from '@mui/material';
import { ArrowBack, Business, AccountTree, Mail, Warehouse, Inventory, ReplayCircleFilled, OpenInNew, ContentCopy, Apps, Send, Article, Folder, InsertDriveFile, Description, Group } from '@mui/icons-material';
import { companyService } from '../index';
import CompanyAreas from '../components/CompanyAreas';
import CompanyCorrespondenceTypes from '../components/CompanyCorrespondenceTypes';
import CompanyWarehouses from '../components/CompanyWarehouses';
import CompanyBoxes from '../components/CompanyBoxes';
import LoadingLogo from '../../../components/LoadingLogo';
import { useAuth } from '../../../hooks/useAuth';

const CompanyDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isOwner } = useAuth();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentTab, setCurrentTab] = useState(0);
  const [copySnackbar, setCopySnackbar] = useState(false);
  const [modulesConfig, setModulesConfig] = useState({});
  const [modulesSaving, setModulesSaving] = useState(false);
  const [modulesSaved, setModulesSaved] = useState(false);

  const MODULE_LIST = [
    { key: 'correspondences', label: 'Correspondencia', icon: Send },
    { key: 'templates', label: 'Plantillas', icon: Article },
    { key: 'proceedings', label: 'Expedientes', icon: Folder },
    { key: 'documents', label: 'Dashboard Doc.', icon: InsertDriveFile },
    { key: 'retentions', label: 'Retención', icon: Description },
    { key: 'entities', label: 'Externos', icon: Group },
    { key: 'warehouses', label: 'Almacenes', icon: Warehouse },
  ];

  useEffect(() => {
    loadCompany();
  }, [id]);

  useEffect(() => {
    if (company) {
      setModulesConfig(company.modules || {});
    }
  }, [company]);

  const loadCompany = async () => {
    try {
      setLoading(true);
      const response = await companyService.getById(id);
      setCompany(response.data);
    } catch (error) {
      setError('Error al cargar la empresa');
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <LoadingLogo size={120} />
      </Box>
    );
  }

  if (error || !company) {
    return (
      <Box>
        <Alert severity="error">{error || 'Empresa no encontrada'}</Alert>
        <Button onClick={() => navigate('/companies')} sx={{ mt: 2 }}>
          Volver a Empresas
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, pb: 2.5, borderBottom: '1px solid #E2E8F0', gap: 1.5 }}>
        {isOwner && (
          <IconButton onClick={() => navigate('/companies')} sx={{ color: '#64748B', '&:hover': { bgcolor: 'rgba(2,81,140,0.06)', color: '#02518C' } }}>
            <ArrowBack />
          </IconButton>
        )}
        <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: 'rgba(2,81,140,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Business sx={{ fontSize: 22, color: '#02518C' }} />
        </Box>
        <Typography sx={{ fontSize: '1.5rem', fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>{company.name}</Typography>
      </Box>

      {/* Company Info Card */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: '16px' }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Identificador
            </Typography>
            <Typography variant="body1" gutterBottom>
              {company.identifier}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Código Corto
            </Typography>
            <Typography variant="body1" gutterBottom>
              {company.short}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Email
            </Typography>
            <Typography variant="body1" gutterBottom>
              {company.email || 'No especificado'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Sitio Web
            </Typography>
            <Typography variant="body1" gutterBottom>
              {company.website || 'No especificado'}
            </Typography>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Máximo de Usuarios
            </Typography>
            <Chip label={company.maxUsers} color="primary" size="small" />
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography variant="subtitle2" color="text.secondary">
              Estado
            </Typography>
            <Chip
              label={company.isActive ? 'Activa' : 'Inactiva'}
              color={company.isActive ? 'success' : 'default'}
              size="small"
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Portal de Correspondencia */}
      <Paper elevation={1} sx={{ p: 3, mb: 3, borderRadius: '16px' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <OpenInNew sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6" fontWeight={600}>
            Portal de Correspondencia
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Enlace público para que usuarios externos radiquen correspondencia en esta empresa.
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Typography
            variant="body2"
            sx={{
              flex: 1,
              minWidth: 0,
              bgcolor: 'grey.100',
              px: 2,
              py: 1,
              borderRadius: 1,
              fontFamily: 'monospace',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {`${window.location.origin}/correspondence/${company.id}/company`}
          </Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<ContentCopy />}
            onClick={() => {
              navigator.clipboard.writeText(`${window.location.origin}/correspondence/${company.id}/company`);
              setCopySnackbar(true);
            }}
          >
            Copiar
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<OpenInNew />}
            href={`/correspondence/${company.id}/company`}
            target="_blank"
          >
            Abrir portal
          </Button>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper elevation={1} sx={{ mb: 2, borderRadius: '16px', overflow: 'hidden' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab icon={<AccountTree />} label="Áreas" iconPosition="start" />
          <Tab icon={<Mail />} label="Tipos de Correspondencia" iconPosition="start" />
          <Tab icon={<Warehouse />} label="Bodegas" iconPosition="start" />
          <Tab icon={<Inventory />} label="Cajas" iconPosition="start" />
          {isOwner && <Tab icon={<Apps />} label="Módulos" iconPosition="start" />}
        </Tabs>
      </Paper>

      {/* Tab Content */}
      <Box>
        {currentTab === 0 && <CompanyAreas companyId={company.id} />}
        {currentTab === 1 && <CompanyCorrespondenceTypes companyId={company.id} />}
        {currentTab === 2 && <CompanyWarehouses companyId={company.id} />}
        {currentTab === 3 && <CompanyBoxes companyId={company.id} />}
        {currentTab === 4 && isOwner && (
          <Paper elevation={1} sx={{ p: 3, borderRadius: '16px' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, gap: 1 }}>
              <Apps sx={{ color: '#02518C' }} />
              <Typography variant="h6" fontWeight={600}>Módulos habilitados</Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Activa o desactiva módulos para esta empresa. Los módulos desactivados desaparecen del menú y sus rutas devuelven 403.
            </Typography>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {MODULE_LIST.map(({ key, label, icon: Icon }) => {
                const enabled = modulesConfig[key] !== false;
                return (
                  <Grid item xs={12} sm={6} md={4} key={key}>
                    <Box
                      onClick={() => setModulesConfig(prev => ({ ...prev, [key]: !enabled }))}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        p: 2,
                        borderRadius: '12px',
                        border: '1.5px solid',
                        borderColor: enabled ? '#02518C' : '#E2E8F0',
                        bgcolor: enabled ? 'rgba(2,81,140,0.04)' : '#FAFAFA',
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        '&:hover': {
                          borderColor: enabled ? '#02518C' : '#CBD5E1',
                          bgcolor: enabled ? 'rgba(2,81,140,0.07)' : '#F1F5F9',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: '8px',
                          bgcolor: enabled ? 'rgba(2,81,140,0.1)' : '#F1F5F9',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Icon sx={{ fontSize: 18, color: enabled ? '#02518C' : '#94A3B8' }} />
                      </Box>
                      <Typography
                        sx={{
                          flex: 1,
                          fontSize: '0.875rem',
                          fontWeight: 500,
                          color: enabled ? '#0F172A' : '#94A3B8',
                        }}
                      >
                        {label}
                      </Typography>
                      <Switch
                        checked={enabled}
                        size="small"
                        color="primary"
                        onClick={(e) => e.stopPropagation()}
                        onChange={(e) => setModulesConfig(prev => ({ ...prev, [key]: e.target.checked }))}
                      />
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
            <Button
              variant="contained"
              disabled={modulesSaving}
              onClick={async () => {
                setModulesSaving(true);
                try {
                  await companyService.update(company.id, { modules: modulesConfig });
                  setModulesSaved(true);
                } finally {
                  setModulesSaving(false);
                }
              }}
              sx={{ borderRadius: '8px', px: 3 }}
            >
              Guardar módulos
            </Button>
          </Paper>
        )}
      </Box>

      <Snackbar
        open={copySnackbar}
        autoHideDuration={3000}
        onClose={() => setCopySnackbar(false)}
        message="Enlace copiado al portapapeles"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
      <Snackbar
        open={modulesSaved}
        autoHideDuration={3000}
        onClose={() => setModulesSaved(false)}
        message="Módulos guardados correctamente"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default CompanyDetail;
